import { UserPlus } from 'lucide-react';

import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useFollow } from '@/hooks/useFollow';
import { useToast } from '@/hooks/useToast';
import { njumpUrl } from '@/lib/owner';
import { Button } from '@/components/ui/button';

interface FollowButtonProps {
  pubkey: string;
  className?: string;
}

/**
 * Logged out → links to the profile on njump.
 * Logged in → appends the pubkey to the visitor's kind-3 contact list.
 */
export function FollowButton({ pubkey, className }: FollowButtonProps) {
  const { user } = useCurrentUser();
  const { toast } = useToast();
  const follow = useFollow();

  // Don't offer to follow yourself.
  if (user?.pubkey === pubkey) return null;

  if (!user) {
    return (
      <Button asChild className={className}>
        <a href={njumpUrl(pubkey)} target="_blank" rel="noopener noreferrer">
          <UserPlus className="size-4" />
          Follow on Nostr
        </a>
      </Button>
    );
  }

  const handleFollow = async () => {
    try {
      const { alreadyFollowing } = await follow.mutateAsync(pubkey);
      toast({
        title: alreadyFollowing ? 'Already following' : 'Following',
        description: alreadyFollowing
          ? 'This account is already in your contact list.'
          : 'Their notes will now show up in your Nostr feed.',
      });
    } catch {
      toast({
        title: 'Follow failed',
        description: 'Could not update your contact list. Please try again.',
        variant: 'destructive',
      });
    }
  };

  return (
    <Button onClick={handleFollow} disabled={follow.isPending} className={className}>
      <UserPlus className="size-4" />
      {follow.isPending ? 'Following…' : 'Follow'}
    </Button>
  );
}
